import * as Tooltip from '@radix-ui/react-tooltip';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appRoutes, buildAiToolEditPath } from '../../app-routes';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { PageHeader } from '../../components/common/PageHeader';
import { partnerApi, type ToolDefinition } from '../../lib/api';
import { AiToolsTabs } from './AiToolsTabs';

type StatusFilter = 'active' | 'inactive' | 'all';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All' },
];

const STATUS_PARAM: Record<StatusFilter, 'active' | 'inactive' | undefined> = {
  active: 'active',
  inactive: 'inactive',
  all: undefined,
};

const STATUS_ACCESS_COPY = {
  active: 'Agents can use this tool.',
  inactive: "Agents can't use this tool.",
} as const;

export const ToolsListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [toolToDeactivate, setToolToDeactivate] = useState<ToolDefinition | null>(null);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['ai-tools', statusFilter],
    queryFn: ({ pageParam }) =>
      partnerApi.getTools({
        cursor: pageParam as string | undefined,
        status: STATUS_PARAM[statusFilter],
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage =>
      lastPage.pagination?.hasNextPage ? (lastPage.pagination.cursor ?? undefined) : undefined,
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => partnerApi.archiveTool(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-tools'] }),
  });

  const tools = data?.pages.flatMap(page => page.tools) ?? [];
  const sortedTools = [...tools].sort((a, b) => a.display_name.localeCompare(b.display_name));

  return (
    <div className="container mx-auto max-w-5xl p-6">
      <AiToolsTabs active="tools" />
      <PageHeader
        title="Tools"
        description="Give Reach agents safe ways to use your API or synced data."
        right={
          <button
            type="button"
            onClick={() => navigate(appRoutes.AI_TOOL_NEW)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            <Plus className="h-4 w-4" />
            New tool
          </button>
        }
      />

      <div className="my-4 inline-flex rounded-lg border bg-muted p-0.5">
        {STATUS_FILTERS.map(filter => (
          <button
            key={filter.value}
            type="button"
            aria-pressed={statusFilter === filter.value}
            onClick={() => setStatusFilter(filter.value)}
            className={
              statusFilter === filter.value
                ? 'rounded-md bg-white px-3 py-1 text-xs font-medium text-foreground shadow-sm'
                : 'rounded-md px-3 py-1 text-xs text-muted-foreground hover:text-foreground'
            }>
            {filter.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : sortedTools.length === 0 ? (
        <div className="rounded-xl border bg-white p-8 text-center text-sm text-muted-foreground">
          No tools yet. Click <strong>New tool</strong> to create one.
        </div>
      ) : (
        <>
          <Tooltip.Provider delayDuration={100}>
            <div className="overflow-hidden rounded-lg border bg-white">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Tool</th>
                    <th className="px-4 py-3 font-medium">What the agent can do</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {sortedTools.map(tool => (
                    <ToolRow
                      key={tool.id}
                      tool={tool}
                      onEdit={() => navigate(buildAiToolEditPath(tool.id))}
                      onArchive={() => setToolToDeactivate(tool)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Tooltip.Provider>
          {hasNextPage && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="rounded-md border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50">
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}

      {toolToDeactivate && (
        <ConfirmDialog
          title="Deactivate tool?"
          confirmLabel="Deactivate"
          onClose={() => setToolToDeactivate(null)}
          onConfirm={() => {
            archiveMutation.mutate(toolToDeactivate.id);
            setToolToDeactivate(null);
          }}>
          <p>
            Deactivating <strong>{humanizeName(toolToDeactivate.display_name)}</strong> removes it from
            your agents — they will no longer be able to use this tool.
          </p>
          <p>If you reference this tool in any Skills, remove those references there too.</p>
          <p>You can reactivate it anytime from the tool&apos;s Status.</p>
        </ConfirmDialog>
      )}
    </div>
  );
};

interface ToolRowProps {
  tool: ToolDefinition;
  onEdit: () => void;
  onArchive: () => void;
}

const ToolRow = ({ tool, onEdit, onArchive }: ToolRowProps) => {
  const isActive = tool.status === 'active';
  const statusClass = isActive
    ? 'rounded bg-green-100 px-2 py-0.5 text-xs text-green-800'
    : 'rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600';
  const statusLabel = isActive ? 'Active' : 'Inactive';
  const statusTooltip = isActive ? STATUS_ACCESS_COPY.active : STATUS_ACCESS_COPY.inactive;

  return (
    <tr className="border-t hover:bg-muted/50">
      <td className="px-4 py-3">
        <button type="button" onClick={onEdit} className="font-medium text-primary hover:underline">
          {humanizeName(tool.display_name)}
        </button>
        <div className="mt-1 font-mono text-xs text-muted-foreground">{tool.display_name}</div>
      </td>
      <td className="px-4 py-3 max-w-md truncate text-muted-foreground">{tool.description}</td>
      <td className="px-4 py-3">
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <span className={statusClass} tabIndex={0}>
              {statusLabel}
            </span>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content side="top" sideOffset={6} className={TOOLTIP_CONTENT_CLASS}>
              {statusTooltip}
              <Tooltip.Arrow className="fill-slate-800" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-1">
          <IconAction
            icon={Pencil}
            tooltip="Edit"
            ariaLabel={`Edit ${tool.display_name}`}
            onClick={onEdit}
          />
          <IconAction
            icon={Trash2}
            tooltip="Deactivate"
            ariaLabel={`Deactivate ${tool.display_name}`}
            onClick={onArchive}
            tone="destructive"
          />
        </div>
      </td>
    </tr>
  );
};

const TOOLTIP_CONTENT_CLASS =
  'z-50 max-w-xs rounded-md bg-slate-800 px-3 py-2 text-xs leading-relaxed text-white shadow-md';

interface IconActionProps {
  icon: LucideIcon;
  tooltip: string;
  ariaLabel: string;
  onClick: () => void;
  tone?: 'default' | 'destructive';
}

const IconAction = ({ icon: Icon, tooltip, ariaLabel, onClick, tone = 'default' }: IconActionProps) => {
  const toneClass =
    tone === 'destructive'
      ? 'text-destructive hover:bg-destructive/10'
      : 'text-muted-foreground hover:bg-muted hover:text-foreground';

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={ariaLabel}
          className={`inline-flex rounded-md p-1.5 ${toneClass}`}>
          <Icon className="h-4 w-4" />
        </button>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content side="top" sideOffset={6} className={TOOLTIP_CONTENT_CLASS}>
          {tooltip}
          <Tooltip.Arrow className="fill-slate-800" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
};

const humanizeName = (name: string) =>
  name
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
